/**
 * Downscale an image file client-side before upload so large photos
 * (e.g. multi-MB camera JPGs) fit the API's size limit. Returns a JPEG
 * (or PNG when the source had transparency-friendly type) capped at
 * `maxDim` on the long edge. Falls back to the original file if decoding
 * fails — the server still enforces its own type/size limits.
 */
export async function downscaleImage(file: File, maxDim = 512): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const type = file.type === 'image/png' || file.type === 'image/webp' ? file.type : 'image/jpeg';
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, 0.9));
    return blob ?? file;
  } catch {
    return file;
  }
}
